/**
 * WastewaterGHGBreakdownChart
 *
 * Horizontal bar chart comparing CH₄ and N₂O contributions (in MTCO₂e).
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import type { WastewaterSummaryRow } from '../../services/wastewaterDataService';

Chart.register(...registerables);

interface WastewaterGHGBreakdownChartProps {
  row: WastewaterSummaryRow | null;
}

const WastewaterGHGBreakdownChart: React.FC<WastewaterGHGBreakdownChartProps> = ({ row }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  const destroyChart = useCallback(() => {
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    if (canvasRef.current) {
      try { const e = Chart.getChart(canvasRef.current); if (e) e.destroy(); } catch {}
    }
  }, []);

  // If exact GHG split isn't in the DB, estimate using ICLEI USCP Appendix F defaults:
  // ~72% N₂O, ~28% CH₄ for California secondary aerobic treatment (by MTCO₂e, given N₂O GWP=273).
  const isEstimated = row != null && (row.ch4_mtco2e == null || row.n2o_mtco2e == null) && row.wastewater_emissions_mtco2e > 0;
  const ch4Derived = row?.ch4_mtco2e ?? (row ? row.wastewater_emissions_mtco2e * 0.28 : null);
  const n2oDerived = row?.n2o_mtco2e ?? (row ? row.wastewater_emissions_mtco2e * 0.72 : null);

  useEffect(() => {
    if (!canvasRef.current || !row || ch4Derived == null || n2oDerived == null) return;
    destroyChart();
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const ch4 = ch4Derived;
    const n2o = n2oDerived;
    const year = row.inventory_year;

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['CH₄ (Methane)', 'N₂O (Nitrous Oxide)'],
        datasets: [
          {
            label: 'MTCO₂e',
            data: [ch4, n2o],
            backgroundColor: ['rgba(59,130,246,0.75)', 'rgba(234,88,12,0.75)'],
            borderColor: ['#3b82f6', '#ea580c'],
            borderWidth: 1.5,
            borderRadius: 4,
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          title: {
            display: true,
            text: isEstimated
            ? `Wastewater Emissions by GHG — ${year} (estimated split)`
            : `Wastewater Emissions by Greenhouse Gas — ${year}`,
            font: { size: 14, weight: 'bold' },
            color: '#1f2937',
            padding: { bottom: 12 },
          },
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const v = ctx.raw as number;
                const total = (ch4Derived ?? 0) + (n2oDerived ?? 0);
                const share = total > 0 ? ((v / total) * 100).toFixed(1) : '?';
                return `${v.toLocaleString('en-US', { maximumFractionDigits: 0 })} MTCO₂e (${share}%)`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { color: 'rgba(0,0,0,0.06)' },
            ticks: {
              color: '#6b7280',
              font: { size: 11 },
              callback: (v) => Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 }),
            },
            title: { display: true, text: 'MTCO₂e', color: '#6b7280', font: { size: 11 } },
          },
          y: {
            grid: { display: false },
            ticks: { color: '#374151', font: { size: 12 } },
          },
        },
      },
    });

    return () => destroyChart();
  }, [row, destroyChart]);

  if (!row || ch4Derived == null || n2oDerived == null) {
    return (
      <div style={{ padding: 32, color: '#6b7280', textAlign: 'center' }}>
        GHG species breakdown not available for this jurisdiction.
      </div>
    );
  }

  return (
    <div>
      <canvas ref={canvasRef} style={{ maxHeight: 200 }} />
      {isEstimated && (
        <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 6, textAlign: 'center' }}>
          Estimated split: exact CH₄/N₂O breakdown not available for this jurisdiction.
          Shown using ICLEI USCP Appendix F default proportions (72% N₂O, 28% CH₄) for secondary aerobic treatment.
        </p>
      )}
    </div>
  );
};

export default WastewaterGHGBreakdownChart;
